import React, { useState, useRef, useEffect } from 'react';
import * as Inovx from '@shared';
import OntologyActions from '../actions/OntologyActions';
import FacilityMapPreview from './FacilityMapPreview';
import OperationProgressDialog from './OperationProgressDialog';
import { InPlaceEditorComponent } from '@syncfusion/ej2-react-inplace-editor';
import { getBlankSiteMap } from '../stores/FacilityStore';

var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var SystemActions = Inovx.Shared.System.Actions;

var PermissionService = Inovx.Shared.Permission.PermissionService;
var MANAGE_FACILITY_PERMISSION = "Facility.Manage";

declare var InovxLoader: any;

const facilityImg = `${UrlUtilities.getBaseApplicationUrl()}/images/uploadIcon.png`;

interface MapConfigurationProps {
    hierarchy: IFacilityHierarchyPart;
    isTabActive: boolean;
}

const hasManagePermission: boolean = PermissionService.checkPermission(MANAGE_FACILITY_PERMISSION);

const MapConfiguration: React.FC<MapConfigurationProps> = ({ hierarchy, isTabActive }) => {
    const mapFileInputRef = useRef<HTMLInputElement>(null);

    const [siteMap, setSiteMap] = useState<ISiteMap | null>(null);
    const [originalSiteMap, setOriginalSiteMap] = useState<ISiteMap | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [edited, setEdited] = useState(false);
    const [refreshKey, setRefreshKey] = useState<string>(null);
    const [operationMessage, setOperationMessage] = useState<string | null>(null);

    useEffect(() => {
        OntologyActions.setOntologyFormDirty(editMode);
    }, [editMode]);

    useEffect(() => {
        setSiteMap(hierarchy.Map);
        setOriginalSiteMap(hierarchy.Map);
        setEditMode(false);
        setEdited(false);
        setRefreshKey(getRefreshKey());
    }, [hierarchy]);

    useEffect(() => {
        setRefreshKey(getRefreshKey());
    }, [isTabActive]);

    // Track edits
    useEffect(() => {
        if (!editMode) return;

        if (!originalSiteMap) {
            setEdited(originalSiteMap != siteMap);
        }
        else {
            setEdited(JSON.stringify(siteMap) !== JSON.stringify(originalSiteMap));
        }
    }, [siteMap, originalSiteMap]);

    const getRefreshKey = () => {
        return `${hierarchy.ID}-${isTabActive ? 1 : 0}`
    }

    const handleMapFileInputChange = () => {
        if (!editMode) return;
        mapFileInputRef.current?.click();
    };

    const handleFileChangeBinary = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!editMode) return;
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;
        
        const reader = new FileReader();
        const updatedSiteMap = getBlankSiteMap();
        updatedSiteMap.MapFile = selectedFile;

        reader.onload = () => {
            const base64String = reader.result as string;
            updatedSiteMap.MapPreview = base64String;
            let imgArray = base64String.split(',');
            if (imgArray.length > 0)  {
                // Load image to get its size
                const img = new window.Image();
                img.onload = function () {
                    updatedSiteMap.ImageSize.X = img.width;
                    updatedSiteMap.ImageSize.Y = img.height;
                    setSiteMap(updatedSiteMap);
                };
                img.src = base64String;
            }
        };
        reader.readAsDataURL(selectedFile);
    };

    const handleRemoveMapImage = () => {
        var messageObject = {
            message: InovxLoader.t('facility:message.confirmDelete'),
            confirmCallback: () => {
                let updatedSiteMap = getBlankSiteMap();
                setSiteMap(updatedSiteMap);
            }
        };
        SystemActions.displayConfirmationMessage(messageObject);
    };

    const setMarkerValue = (value: number, axis: 'X' | 'Y', marker: 'translation' | 'scale' | 'rotation') => {
        if (!editMode) return;
        const updatedSiteMap = { ...siteMap };
        if (axis === 'X') {
            if (marker === 'translation') {
                updatedSiteMap.WorldTranslation = { X: value, Y: updatedSiteMap.WorldTranslation.Y };
            } else if (marker === 'scale') {
                updatedSiteMap.WorldScale = { X: value, Y: updatedSiteMap.WorldScale.Y };
            }
        } else {
            if (marker === 'translation') {
                updatedSiteMap.WorldTranslation = { X: updatedSiteMap.WorldTranslation.X, Y: value };
            } else if (marker === 'scale') {
                updatedSiteMap.WorldScale = { X: updatedSiteMap.WorldScale.X, Y: value };
            }
        }
        setSiteMap(updatedSiteMap);
    };

    const setMapOrigin = (origin: any, marker: 'blue' | 'green' | 'red') => {
        if (!editMode) return;
        const updatedSiteMap = { ...siteMap };
        if (marker === 'blue') {
            updatedSiteMap.ImageTranslation = { X: origin.lng, Y: origin.lat };
        } else if (marker === 'green') {
            updatedSiteMap.ImageScale = { X: origin.lng, Y: origin.lat };
        }
        setSiteMap(updatedSiteMap);
    }

    const previewMapImage = siteMap?.MapPreview;
    let mapThumbnail = facilityImg;
    if (previewMapImage) {
        mapThumbnail = previewMapImage;
    }

    // Edit/Save/Cancel logic
    const handleEdit = () => {
        setEditMode(true);
        setEdited(false);
        setOriginalSiteMap(siteMap ? { ...siteMap } : null);
    };

    const cancelEdit = (resetData: boolean) => {
        if (resetData) {
            setSiteMap(originalSiteMap ? { ...originalSiteMap } : null);
        }
        setEditMode(false);
        setEdited(false);
    };

    const handleSave = async () => {
        if (!edited) return;

        const newHierarchyData = { ...hierarchy, Map: { ...siteMap } };
        setOperationMessage(InovxLoader.t('facility:message.savingOntologyMap'));
        try {
            await OntologyActions.editOntologyPart(newHierarchyData);
            setOriginalSiteMap(siteMap ? { ...siteMap } : null);
            cancelEdit(false);
        } finally {
            setOperationMessage(null);
        }
    };

    const handleCancel = () => {
        if (edited) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: async () => {
                    await handleSave();
                },
                cancelCallback: () => {
                    cancelEdit(true);
                }
            });
        } else {
            cancelEdit(true);
        }
    };

    const isMapConfigurationEnabled = hierarchy.MapType != "inherit" && isTabActive;

    return (
        <div className="map-configuration">
            <OperationProgressDialog visible={!!operationMessage} message={operationMessage || undefined} />
            {isMapConfigurationEnabled ? (<div key={refreshKey}>
                {previewMapImage && <>
                    <div className="map-config-row">
                        <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/marker-icon.png`}
                            alt={InovxLoader.t("facility:form.marker1Icon")} />
                        <div className="form-switch-label">
                            <label className="translation-marker-label">
                                {InovxLoader.t("facility:hierarchy.marker1")}
                            </label>
                        </div>
                        <div className="map-config-coords">
                            <div>
                                <span>X:</span>
                                <InPlaceNumericEditor value={siteMap?.WorldTranslation.X} editable={editMode} onChange={val => setMarkerValue(val, 'X', 'translation')} />
                            </div>
                            <div>
                                <span>Y:</span>
                                <InPlaceNumericEditor value={siteMap?.WorldTranslation.Y} editable={editMode} onChange={val => setMarkerValue(val, 'Y', 'translation')} />
                            </div>
                        </div>
                    </div>
                    <div className="map-config-row">
                        <img src={`${UrlUtilities.getBaseApplicationUrl()}/images/leaflet/marker-icon-green.png`}
                            alt={InovxLoader.t("facility:form.marker2Icon")} />
                        <div className="form-switch-label">
                            <label className="scale-marker-label">
                                {InovxLoader.t("facility:hierarchy.marker2")}
                            </label>
                        </div>
                        <div className="map-config-coords">
                            <div>
                                <span>X:</span>
                                <InPlaceNumericEditor value={siteMap?.WorldScale.X} editable={editMode} onChange={val => setMarkerValue(val, 'X', 'scale')} />
                            </div>
                            <div>
                                <span>Y:</span>
                                <InPlaceNumericEditor value={siteMap?.WorldScale.Y} editable={editMode} onChange={val => setMarkerValue(val, 'Y', 'scale')} />
                            </div>
                        </div>
                    </div>
                </>}
                <div>
                    <div className="map-config-title">{InovxLoader.t("facility:hierarchy.mapTitle")}</div>
                    {previewMapImage &&
                        <div className="map-preview-container cursor-pointer">
                            {editMode && <div onClick={handleRemoveMapImage} className="remove-img-btn"><i className="fa fa-times-circle" aria-hidden="true"></i></div>}
                            <FacilityMapPreview siteMap={siteMap} editable={editMode} onOriginChange={(origin, marker) => setMapOrigin(origin, marker)} />
                        </div>
                    }
                    {!previewMapImage && <div onClick={handleMapFileInputChange} className={`thumbnail-container${editMode ? ' cursor-pointer' : ''}`}>
                        <input
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            ref={mapFileInputRef}
                            onChange={handleFileChangeBinary}
                        />
                        <img src={mapThumbnail} alt={InovxLoader.t("facility:hierarchy.thumbnailAlt")} />
                        {editMode && <div className="upload-btn"><i className="fa fa-plus-circle" aria-hidden="true"></i></div>}
                    </div>}
                </div>
                {hasManagePermission && <div className="map-config-actions">
                    {!editMode ? (
                        <a id="facilityApp-edit-btn" onClick={handleEdit} className="btn btn-primary">{InovxLoader.t("facility:preferences.editButton")}</a>
                    ) : (
                        <div>
                            <div className="d-flex j-cc">
                                <div>
                                    <a id="facilityApp-save-btn" onClick={handleSave} className={`btn btn-primary${!edited ? ' disabled' : ''}`}>{InovxLoader.t("facility:preferences.saveButton")}</a>
                                </div>
                                <div className="ml-1">
                                    <a id="facilityApp-cancel-btn" onClick={handleCancel} className="btn btn-default">{InovxLoader.t("facility:preferences.cancelButton")}</a>
                                </div>
                            </div>
                        </div>
                    )}
                </div>}
            </div>) : (
                <div>
                    {InovxLoader.t("facility:message.mapInherited")}
                </div>
            )}
        </div>
    );
};

interface InPlaceNumericEditorProps {
    value: number;
    editable: boolean;
    onChange: (value: number) => void;
}

const InPlaceNumericEditor: React.FC<InPlaceNumericEditorProps> = ({ value, editable, onChange }) => (
    <InPlaceEditorComponent
        key={editable ? 'editable' : 'readonly'}
        mode="Popup"
        type="Numeric"
        value={value}
        showButtons={true}
        submitOnEnter={true}
        editableOn="Click"
        actionOnBlur="Submit"
        cssClass="inplace-numeric-editor"
        model={{ decimals: 3, showSpinButton: false, enabled: editable }}
        change={(e: any) => onChange(Number(e.value))}
        disabled={!editable}
    />
);

export default MapConfiguration;
